import { useTranslation } from 'react-i18next'
import { AlertTriangle, Download } from 'lucide-react'
import type { UnresolvedReference } from '../../types/relationship-import'

const MAX_VISIBLE_ROWS = 5

interface UnresolvedReferencesPanelProps {
  references: UnresolvedReference[]
  onExport: () => void
}

function ReferenceRow({ reference }: { reference: UnresolvedReference }): React.ReactElement {
  const { t } = useTranslation('cases')
  const roleKey =
    reference.role === 'source'
      ? 'infrastructure.importRelationship.roleSource'
      : 'infrastructure.importRelationship.roleTarget'

  return (
    <tr className="border-b border-slate-100 dark:border-slate-800">
      <td className="py-2 pr-4 font-mono text-xs text-slate-700 dark:text-slate-300">
        {reference.hostname ?? '-'}
      </td>
      <td className="py-2 pr-4 font-mono text-xs text-slate-700 dark:text-slate-300">
        {reference.ip ?? '-'}
      </td>
      <td className="py-2 pr-4 text-sm text-slate-600 dark:text-slate-400">{t(roleKey)}</td>
      <td className="py-2 text-xs text-slate-500 dark:text-slate-400">
        {reference.rows.join(', ')}
      </td>
    </tr>
  )
}

function ReferenceTableHeader(): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <tr className="border-b border-amber-200 dark:border-amber-800">
      <th className="py-2 pr-4 text-left text-xs font-medium text-amber-700 dark:text-amber-400">
        {t('infrastructure.importRelationship.unresolvedHostname')}
      </th>
      <th className="py-2 pr-4 text-left text-xs font-medium text-amber-700 dark:text-amber-400">
        {t('infrastructure.importRelationship.unresolvedIp')}
      </th>
      <th className="py-2 pr-4 text-left text-xs font-medium text-amber-700 dark:text-amber-400">
        {t('infrastructure.importRelationship.unresolvedRole')}
      </th>
      <th className="py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-400">
        {t('infrastructure.importRelationship.unresolvedRows')}
      </th>
    </tr>
  )
}

export function UnresolvedReferencesPanel({
  references,
  onExport,
}: UnresolvedReferencesPanelProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const visibleRefs = references.slice(0, MAX_VISIBLE_ROWS)
  const hiddenCount = references.length - visibleRefs.length

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <h4 className="font-medium text-amber-800 dark:text-amber-300">
            {t('infrastructure.importRelationship.unresolvedReferences', {
              count: references.length,
            })}
          </h4>
        </div>
        <button
          onClick={onExport}
          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-900/40"
        >
          <Download className="h-3.5 w-3.5" />
          {t('infrastructure.importRelationship.exportUnresolved')}
        </button>
      </div>
      <p className="mb-3 text-sm text-amber-700 dark:text-amber-400">
        {t('infrastructure.importRelationship.unresolvedReferencesDesc')}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <ReferenceTableHeader />
          </thead>
          <tbody>
            {visibleRefs.map((reference, index) => (
              <ReferenceRow key={index} reference={reference} />
            ))}
          </tbody>
        </table>
      </div>
      {hiddenCount > 0 && (
        <p className="mt-2 text-xs text-amber-600 opacity-75 dark:text-amber-400">
          {t('infrastructure.importRelationship.andMore', { count: hiddenCount })}
        </p>
      )}
    </div>
  )
}
